/**
 * AM-4364 — the precedence primitive shared by every feature the `FeatureResolver` owns.
 *
 * A UX-affecting decision is resolved from two configuration sources, in order:
 *   1. a durable settings flag (server-overridable) — when defined it wins outright;
 *   2. the transient experiment variant — mapped to a value via `fromVariant`;
 *   3. a hard-coded fallback when neither source has an opinion.
 *
 * This is what lets any experiment be "promoted" to a permanent default uniformly: set the
 * flag and it overrides the variant for everyone, no consumer changes required.
 */
export interface FeatureResolutionInput<T> {
  /**
   * The durable settings flag. `undefined` means "no override — defer to the variant". Any
   * defined value (including a falsy/`'none'`-style value) wins, so a flag can also force a
   * feature OFF as a kill switch.
   */
  flag: T | undefined;
  /** The active experiment variant code, or `null` when the client is in no variant. */
  variant: string | null;
  /**
   * Maps a variant code to the value it activates. Return `undefined` for "this variant has
   * no opinion" so resolution falls through to `fallback` (do NOT return the fallback here).
   */
  fromVariant: (variant: string | null) => T | undefined;
  /** Value used when neither the flag nor the variant resolves anything. */
  fallback: T;
}

/** Resolves a feature's effective value from its settings flag and experiment variant. */
export function resolveFeature<T>({ flag, variant, fromVariant, fallback }: FeatureResolutionInput<T>): T {
  if (flag !== undefined) {
    return flag;
  }
  return fromVariant(variant) ?? fallback;
}
